/**
 * auto-warranty.js — Virtual warranty auto-add for the Impact theme (Maestrooo)
 * ----------------------------------------------------------------------------
 * Behaviour:
 *   - When the cart contains >= 1 real product, ensure the warranty product
 *     (variant configured in Theme settings) is present with quantity exactly 1.
 *   - When all real products are removed (real item count drops to 0), remove
 *     the warranty product from the cart.
 *
 * Integration notes (verified against this theme's assets/theme.js):
 *   - We sync on the `cart:change` event (dispatched on document.documentElement,
 *     bubbles to document). Its detail.cart is the full, up-to-date cart object
 *     so we read item quantities straight from it without an extra /cart.js call.
 *   - We also sync once on DOMContentLoaded. This covers returning visitors with
 *     a pre-existing cart AND the cases where Impact does a full page reload
 *     instead of firing cart:change (quantity change on the /cart page, or
 *     add-to-cart when cart_type is "page" — both reload, then this re-syncs).
 *   - After mutating the cart we dispatch `cart:refresh` on
 *     document.documentElement (bubbles) so Impact rebuilds the cart drawer DOM
 *     and refreshes the cart count.
 *
 * This file is intentionally self-contained and touches no core theme files.
 * The feature is gated behind a theme setting (Theme settings → Cart →
 * "Enable automatic warranty"); when disabled, theme.liquid does not load this
 * script at all. The warranty variant id is supplied via window.autoWarrantyConfig.
 */
(function () {
  'use strict';

  // The warranty product's variant id, injected from the theme setting via
  // window.autoWarrantyConfig.variantId. The setting is a text field so it
  // arrives as a string; Number() makes it comparable to cart line
  // `variant_id` values (which come back as numbers). If the value is missing
  // or not a valid number, we bail out rather than risk mutating the cart.
  var config = window.autoWarrantyConfig || {};
  var WARRANTY_VARIANT_ID = Number(config.variantId);
  var WARRANTY_QUANTITY = 1;

  if (!WARRANTY_VARIANT_ID || Number.isNaN(WARRANTY_VARIANT_ID)) {
    console.warn('[auto-warranty] No valid variant id configured; feature inactive.');
    return;
  }

  // Locale-safe route prefix. Impact always uses Shopify.routes.root (e.g. "/"
  // or "/en-gb/"); fall back to "/" if it is somehow unavailable.
  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  // Re-entrancy guard: our own /cart/add.js and /cart/change.js calls cause the
  // theme to fire cart:change again. Without this flag the handler would react
  // to its own mutation and loop.
  var syncing = false;

  /**
   * Sum the quantities of all REAL items in a cart, i.e. every line whose
   * variant is NOT the warranty variant. Excluding the warranty line is what
   * prevents the "cart can never empty / infinite loop" failure mode.
   */
  function countRealItems(cart) {
    if (!cart || !Array.isArray(cart.items)) {
      return 0;
    }
    var total = 0;
    for (var i = 0; i < cart.items.length; i++) {
      var item = cart.items[i];
      if (Number(item.variant_id) !== WARRANTY_VARIANT_ID) {
        total += item.quantity;
      }
    }
    return total;
  }

  /** Find the warranty line in a cart, or null if it isn't present. */
  function findWarrantyLine(cart) {
    if (!cart || !Array.isArray(cart.items)) {
      return null;
    }
    for (var i = 0; i < cart.items.length; i++) {
      if (Number(cart.items[i].variant_id) === WARRANTY_VARIANT_ID) {
        return cart.items[i];
      }
    }
    return null;
  }

  function getCart() {
    return fetch(root() + 'cart.js', {
      headers: { Accept: 'application/json' }
    }).then(function (res) {
      return res.json();
    });
  }

  function addWarranty() {
    return fetch(root() + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: WARRANTY_VARIANT_ID, quantity: WARRANTY_QUANTITY })
    });
  }

  /**
   * Remove (or correct) the warranty line by setting its quantity.
   * We address the line by its unique `key` when available (the identifier
   * /cart/change.js expects); we fall back to the variant id otherwise.
   */
  function changeWarranty(warrantyLine, quantity) {
    var id = warrantyLine && warrantyLine.key ? warrantyLine.key : WARRANTY_VARIANT_ID;
    return fetch(root() + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: id, quantity: quantity })
    });
  }

  /**
   * NOTE ON ORDERING ("warranty must be the last line"):
   * Shopify's Cart AJAX API has no parameter to set a line's position, and the
   * platform's line ordering is inconsistent (it flips between newest-first and
   * oldest-first, including after checkout visits). So we do NOT try to reorder
   * the cart from JS. Instead, the warranty is forced to render last in the
   * theme's Liquid templates (sections/cart-drawer.liquid and main-cart.liquid),
   * which is deterministic and immune to that platform churn. This file only
   * keeps the warranty present (qty 1) / removes it; position is a render concern.
   */

  /** Tell Impact to refresh the cart count and rebuild the drawer UI. */
  function notifyThemeRefresh() {
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:refresh', { bubbles: true })
    );
  }

  /**
   * Core reconciliation. Given the current cart state, decide whether to add,
   * remove, or fix the quantity of the warranty product, then make exactly one
   * mutation if needed. Returns a Promise that resolves true if the cart was
   * mutated, false otherwise.
   */
  function reconcile(cart) {
    var realCount = countRealItems(cart);
    var warrantyLine = findWarrantyLine(cart);
    var hasWarranty = warrantyLine !== null;

    if (realCount > 0) {
      // Should have exactly one warranty unit.
      if (!hasWarranty) {
        return addWarranty().then(function () {
          return true;
        });
      }
      if (warrantyLine.quantity !== WARRANTY_QUANTITY) {
        // Never accumulate: clamp back to 1 regardless of real-item count.
        return changeWarranty(warrantyLine, WARRANTY_QUANTITY).then(function () {
          return true;
        });
      }
      return Promise.resolve(false);
    }

    // realCount === 0 → no real products left; warranty must go.
    if (hasWarranty) {
      return changeWarranty(warrantyLine, 0).then(function () {
        return true;
      });
    }
    return Promise.resolve(false);
  }

  /**
   * Run a sync. If a cart object is provided (from cart:change detail) it is
   * used directly; otherwise the current cart is fetched. Guards against
   * re-entry and always refreshes the theme UI when a mutation occurred.
   */
  function sync(cart) {
    if (syncing) {
      return;
    }
    syncing = true;

    var start = cart ? Promise.resolve(cart) : getCart();

    start
      .then(function (currentCart) {
        return reconcile(currentCart);
      })
      .then(function (mutated) {
        if (mutated) {
          notifyThemeRefresh();
        }
      })
      .catch(function (err) {
        // Never let a transient cart error break the page; just log it.
        console.error('[auto-warranty] sync failed:', err);
      })
      .then(function () {
        syncing = false;
      });
  }

  // Trigger on Impact's cart change event. detail.cart is the full updated cart.
  document.addEventListener('cart:change', function (event) {
    var cart = event && event.detail ? event.detail.cart : null;
    sync(cart);
  });

  // Initial sync for returning visitors / post-reload states.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      sync();
    });
  } else {
    // Script ran after DOM was already parsed (defer can resolve very late).
    sync();
  }
})();
